
import React, { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Cookie, ChevronDown, ChevronUp, Shield, BarChart3, Megaphone, Settings2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import type { CookiePreferences } from "./CookieSettings";

interface MobileCookieDisclaimerProps {
  isVisible: boolean;
  onAccept: (preferences: CookiePreferences) => void;
  onClose: () => void;
}

const MobileCookieDisclaimer = ({ isVisible, onAccept, onClose }: MobileCookieDisclaimerProps) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });

  const isAr = language === 'ar';

  const handleAcceptAll = () => {
    onAccept({ necessary: true, analytics: true, marketing: true, preferences: true });
  };

  const handleReject = () => {
    onAccept({ necessary: true, analytics: false, marketing: false, preferences: false });
  };

  const handleSavePreferences = () => {
    onAccept(preferences);
    setShowSettings(false);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return;
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const cookieCategories = [
    {
      key: 'necessary' as const,
      icon: Shield,
      label: isAr ? 'ضرورية' : 'Necessary',
      desc: isAr ? 'مطلوبة لعمل الموقع' : 'Required for the site to work',
      locked: true,
    },
    {
      key: 'analytics' as const,
      icon: BarChart3,
      label: isAr ? 'تحليلات' : 'Analytics',
      desc: isAr ? 'تساعدنا على فهم الاستخدام' : 'Help us understand usage',
      locked: false,
    },
    {
      key: 'marketing' as const,
      icon: Megaphone,
      label: isAr ? 'تسويق' : 'Marketing',
      desc: isAr ? 'إعلانات مخصصة' : 'Personalized ads',
      locked: false,
    },
    {
      key: 'preferences' as const,
      icon: Settings2,
      label: isAr ? 'تفضيلات' : 'Preferences',
      desc: isAr ? 'تذكر إعداداتك' : 'Remember your settings',
      locked: false,
    },
  ];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="w-full max-w-md max-h-[90vh] overflow-auto"
          >
            <div className="bg-card border border-border/60 shadow-2xl shadow-black/30 rounded-2xl overflow-hidden">
              {/* Accent bar */}
              <div className="h-1 w-full bg-gradient-to-r from-primary via-accent to-primary" />

              <div className="px-4 py-5">
                {/* Header */}
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 border border-primary/20 flex items-center justify-center">
                    <Cookie className="h-4 w-4 text-primary" />
                  </div>
                  <h3 className="font-bold text-base text-foreground">
                    {isAr ? 'نستخدم ملفات تعريف الارتباط' : 'We use cookies'}
                  </h3>
                </div>

                <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                  {isAr
                    ? 'لتحسين تجربتك وتوفير محتوى مخصص. يمكنك تخصيص تفضيلاتك في أي وقت.'
                    : 'To enhance your experience and provide personalized content. Customize your preferences anytime.'}
                </p>

                {/* Settings accordion */}
                <AnimatePresence>
                  {showSettings && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden mb-4"
                    >
                      <div className="space-y-2">
                        {cookieCategories.map(({ key, icon: Icon, label, desc, locked }) => (
                          <button
                            key={key}
                            onClick={() => togglePreference(key)}
                            disabled={locked}
                            className="w-full flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-muted/30 text-left"
                          >
                            <Icon className="h-4 w-4 text-primary shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground">{label}</p>
                              <p className="text-[11px] text-muted-foreground">{desc}</p>
                            </div>
                            <div className={`w-10 h-5 rounded-full transition-colors shrink-0 flex items-center px-0.5 ${
                              preferences[key] ? 'bg-primary justify-end' : 'bg-muted-foreground/30 justify-start'
                            } ${locked ? 'opacity-60' : ''}`}>
                              <div className="w-4 h-4 rounded-full bg-background shadow-sm" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Buttons */}
                <div className="space-y-2">
                  {showSettings ? (
                    <Button
                      onClick={handleSavePreferences}
                      className="w-full h-11 text-sm font-semibold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground"
                    >
                      {isAr ? 'حفظ التفضيلات' : 'Save Preferences'}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleAcceptAll}
                      className="w-full h-11 text-sm font-semibold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground"
                    >
                      {isAr ? 'قبول الكل' : 'Accept All'}
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    onClick={() => setShowSettings(!showSettings)}
                    className="w-full h-10 text-xs font-medium border-border/60 hover:bg-accent/50"
                  >
                    {showSettings
                      ? (isAr ? 'إخفاء الإعدادات' : 'Hide Settings')
                      : (isAr ? 'تخصيص' : 'Customize')}
                    {showSettings ? <ChevronDown className="ml-1.5 h-3.5 w-3.5" /> : <ChevronUp className="ml-1.5 h-3.5 w-3.5" />}
                  </Button>

                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      onClick={handleReject}
                      className="flex-1 h-10 text-xs text-muted-foreground hover:text-foreground hover:bg-accent/40"
                    >
                      {isAr ? 'رفض' : 'Reject'}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => navigate('/privacy-policy')}
                      className="flex-1 h-10 text-xs text-muted-foreground hover:text-foreground hover:bg-accent/40"
                    >
                      {isAr ? 'الخصوصية' : 'Privacy Policy'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileCookieDisclaimer;
